<template>
    <UserLayout :page-title="t('aiRecommendations')" :page-section="t('smartInsights')">
        <div class="ari-wrap">

            <!-- Welcome strip + Generate button -->
            <div class="ari-welcome anim-up" style="--d:0ms">
                <div class="ari-welcome-left">
                    <div class="ari-welcome-icon">
                        <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                        </svg>
                    </div>
                    <div>
                        <div class="ari-welcome-eyebrow">{{ t('smartInsights') }}</div>
                        <div class="ari-welcome-title">{{ t('aiRecommendations') }}</div>
                        <p class="ari-welcome-sub">{{ t('personalizedInvestmentInsights') }}</p>
                    </div>
                </div>
                <button @click="generateRecommendations" :disabled="isGenerating" class="ari-gen-btn">
                    <svg v-if="!isGenerating" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                    </svg>
                    <svg v-else class="ari-spin" width="16" height="16" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="ari-hide-xs">{{ isGenerating ? t('generating') : t('generateNew') }}</span>
                    <span class="ari-show-xs">{{ isGenerating ? t('generating') : t('generate') }}</span>
                </button>
            </div>

            <!-- Stats row -->
            <div class="ari-stats anim-up" style="--d:60ms">
                <div class="ari-stat" style="--ac:167,93,237">
                    <div class="ari-stat-glow"></div>
                    <div class="ari-stat-label">{{ t('totalRecommendations') || 'Total' }}</div>
                    <div class="ari-stat-val">{{ stats?.total || 0 }}</div>
                </div>
                <div class="ari-stat" style="--ac:34,197,94">
                    <div class="ari-stat-glow"></div>
                    <div class="ari-stat-label">{{ t('active') }}</div>
                    <div class="ari-stat-val">{{ stats?.active || 0 }}</div>
                </div>
                <div class="ari-stat" style="--ac:251,191,36">
                    <div class="ari-stat-glow"></div>
                    <div class="ari-stat-label">{{ t('unread') || 'Unread' }}</div>
                    <div class="ari-stat-val">{{ stats?.unread || 0 }}</div>
                </div>
                <div class="ari-stat" style="--ac:59,130,246">
                    <div class="ari-stat-glow"></div>
                    <div class="ari-stat-label">{{ t('applied') || 'Applied' }}</div>
                    <div class="ari-stat-val">{{ stats?.applied || 0 }}</div>
                </div>
            </div>

            <!-- List card -->
            <div class="ari-card anim-up" style="--d:100ms">
                <div class="ari-card-glow"></div>

                <!-- Card head -->
                <div class="ari-card-head">
                    <div>
                        <div class="ari-card-title">{{ t('yourRecommendations') }}</div>
                        <div class="ari-card-sub">{{ t('aiPoweredInvestmentSuggestions') }}</div>
                    </div>
                    <div class="ari-total-badge">{{ recommendations?.meta?.total || 0 }} {{ t('total') }}</div>
                </div>

                <!-- Filter pills -->
                <div class="ari-filters">
                    <button @click="filters.type = ''; applyFilters()"
                        class="ari-pill" :class="!filters.type ? 'ari-pill--active' : ''">
                        {{ t('allTypes') }}
                    </button>
                    <button @click="filters.type = 'investment_plan'; applyFilters()"
                        class="ari-pill" :class="filters.type === 'investment_plan' ? 'ari-pill--active' : ''">
                        {{ t('investmentPlan') }}
                    </button>
                    <button @click="filters.type = 'reinvestment'; applyFilters()"
                        class="ari-pill" :class="filters.type === 'reinvestment' ? 'ari-pill--active' : ''">
                        {{ t('reinvestment') }}
                    </button>
                    <button @click="filters.status = filters.status === 'active' ? '' : 'active'; applyFilters()"
                        class="ari-pill" :class="filters.status === 'active' ? 'ari-pill--active' : ''">
                        {{ t('activeOnly') }}
                    </button>
                </div>

                <!-- Empty state -->
                <div v-if="!recommendations.data || recommendations.data.length === 0" class="ari-empty">
                    <div class="ari-empty-icon">
                        <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                        </svg>
                    </div>
                    <p class="ari-empty-title">{{ t('noRecommendations') }}</p>
                    <p class="ari-empty-sub">{{ t('clickGenerateToGetPersonalizedInsights') }}</p>
                </div>

                <!-- Recommendation rows -->
                <div v-else class="ari-list">
                    <div v-for="rec in recommendations.data" :key="rec.id" class="ari-rec">
                        <div class="ari-rec-main">
                            <div class="ari-rec-icon">
                                <svg v-if="rec.type === 'investment_plan'" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                </svg>
                                <svg v-else width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                            </div>

                            <div class="ari-rec-body">
                                <div class="ari-rec-top">
                                    <span v-if="!rec.is_read" class="ari-unread-dot"></span>
                                    <h3 class="ari-rec-title">{{ rec.title }}</h3>
                                    <span class="ari-rec-date">{{ formatDate(rec.created_at) }}</span>
                                </div>
                                <p class="ari-rec-desc">{{ rec.description }}</p>

                                <div class="ari-rec-stats">
                                    <div class="ari-rec-stat">
                                        <span class="ari-rec-stat-label">{{ t('confidence') }}</span>
                                        <span class="ari-rec-stat-val">{{ rec.confidence_score }}%</span>
                                    </div>
                                    <div class="ari-rec-stat">
                                        <span class="ari-rec-stat-label">{{ t('priority') }}</span>
                                        <span class="ari-rec-stat-val" :class="getPriorityClass(rec.priority)">{{ rec.priority }}</span>
                                    </div>
                                    <div class="ari-rec-stat">
                                        <span class="ari-rec-stat-label">{{ t('type') }}</span>
                                        <span class="ari-rec-stat-val">{{ formatType(rec.type) }}</span>
                                    </div>
                                </div>

                                <div class="ari-rec-actions">
                                    <Link :href="`/user/ai-recommendations/${rec.id}`" class="ari-btn-view">
                                        {{ t('viewDetails') }}
                                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                        </svg>
                                    </Link>
                                    <button v-if="rec.status === 'active'" @click="dismissRecommendation(rec.id)" class="ari-btn-ghost">
                                        {{ t('dismiss') }}
                                    </button>
                                    <span v-else class="ari-status-tag" :class="getStatusClass(rec.status)">
                                        {{ rec.status }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="recommendations.data && recommendations.data.length > 0 && recommendations.meta.last_page > 1"
                    class="ari-pagination">
                    <div class="ari-page-info">
                        {{ t('showing') }} {{ recommendations.meta.from || 0 }} {{ t('to') }} {{ recommendations.meta.to || 0 }} {{ t('of') }} {{ recommendations.meta.total || 0 }} {{ t('results') }}
                    </div>
                    <div class="ari-page-btns">
                        <button v-for="(link, index) in paginationLinks" :key="index"
                            :disabled="!link.url"
                            class="ari-page-btn"
                            :class="link.active ? 'ari-page-btn--active' : !link.url ? 'ari-page-btn--disabled' : ''"
                            @click="goToPage(link.url)"
                            v-html="link.label" />
                    </div>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<script setup>
import { ref, computed, h } from 'vue';
import { usePage, Link, router } from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import { useToast } from "@/composables/useToast.js";
import { useTranslation } from '@/composables/useTranslation';

const props = defineProps({
    recommendations: {
        type: Object,
        required: true,
        default: () => ({
            data: [],
            meta: {
                total: 0,
                current_page: 1,
                per_page: 10,
                last_page: 1,
                from: null,
                to: null,
                links: []
            }
        })
    },
    stats: {
        type: Object,
        required: true,
        default: () => ({
            total: 0,
            active: 0,
            unread: 0,
            applied: 0
        })
    }
});

const { showToast } = useToast();
const { t } = useTranslation();
const page = usePage();

const isGenerating = ref(false);
const filters = ref({
    type: '',
    priority: '',
    status: ''
});

const primaryColor = computed(() => page.props.primaryColor || '#86efac');

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 134, g: 239, b: 172 };
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return "#" + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const statsCards = computed(() => [
    {
        label: t('totalRecommendations') || 'Total',
        value: props.stats?.total || 0,
        icon: () => h('svg', { class: 'w-full h-full', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
            h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' })
        ]),
        badge: null
    },
    {
        label: t('active') || 'Active',
        value: props.stats?.active || 0,
        icon: () => h('svg', { class: 'w-full h-full', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
            h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M13 10V3L4 14h7v7l9-11h-7z' })
        ]),
        badge: (props.stats?.active || 0) > 0 ? t('live') || 'Live' : null
    },
    {
        label: t('unread') || 'Unread',
        value: props.stats?.unread || 0,
        icon: () => h('svg', { class: 'w-full h-full', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
            h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' })
        ]),
        badge: (props.stats?.unread || 0) > 0 ? t('new') || 'New' : null
    },
    {
        label: t('applied') || 'Applied',
        value: props.stats?.applied || 0,
        icon: () => h('svg', { class: 'w-full h-full', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
            h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' })
        ]),
        badge: null
    }
]);

const paginationLinks = computed(() => {
    return props.recommendations?.meta?.links || [];
});

const generateRecommendations = () => {
    isGenerating.value = true;
    router.post('/user/ai-recommendations/generate', {}, {
        onSuccess: () => {
            showToast(t('recommendationsGenerated') || 'Recommendations generated successfully', 'success');
        },
        onError: () => {
            showToast(t('failedToGenerate') || 'Failed to generate recommendations', 'error');
        },
        onFinish: () => {
            isGenerating.value = false;
        }
    });
};

const dismissRecommendation = (id) => {
    router.post(`/user/ai-recommendations/${id}/dismiss`, {}, {
        onSuccess: () => {
            showToast(t('recommendationDismissed') || 'Recommendation dismissed', 'success');
        },
        onError: () => {
            showToast(t('failedToDismiss') || 'Failed to dismiss recommendation', 'error');
        }
    });
};

const applyFilters = () => {
    router.get('/user/ai-recommendations', filters.value, {
        preserveState: true,
        preserveScroll: true
    });
};

const goToPage = (url) => {
    if (!url || typeof url !== 'string') return;
    router.get(url, {}, {
        preserveState: true,
        preserveScroll: true,
        onError: () => showToast(t('failedToLoadPage') || 'Failed to load page', 'error')
    });
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatType = (type) => {
    return type === 'investment_plan' ? t('investmentPlan') || 'Investment Plan' : t('reinvestment') || 'Reinvestment';
};

const getPriorityClass = (priority) => {
    const classes = {
        high: 'ari-priority--high',
        medium: 'ari-priority--medium',
        low: 'ari-priority--low'
    };
    return classes[priority] || 'ari-priority--low';
};

const getStatusClass = (status) => {
    const classes = {
        active: 'ari-status--active',
        dismissed: 'ari-status--dismissed',
        applied: 'ari-status--applied'
    };
    return classes[status] || 'ari-status--dismissed';
};
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }
.ari-spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to{ transform: rotate(360deg); } }

.ari-wrap {
    width: 100%; padding: 24px 16px; box-sizing: border-box;
    display: flex; flex-direction: column; gap: 20px;
}
@media(min-width:640px){ .ari-wrap{ padding: 32px 24px; } }
@media(min-width:1024px){ .ari-wrap{ padding: 40px 32px; } }

/* welcome */
.ari-welcome {
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px;
    background: rgba(20,10,40,0.92); border: 1px solid rgba(167,93,237,0.18);
    border-radius: 16px; padding: 18px 24px; position: relative; overflow: hidden;
}
.ari-welcome::before { content: ''; position: absolute; inset: 0 0 auto 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.ari-welcome-left { display: flex; align-items: flex-start; gap: 14px; }
.ari-welcome-icon { width: 44px; height: 44px; border-radius: 12px; background: rgba(167,93,237,0.14); border: 1px solid rgba(167,93,237,0.25); display: flex; align-items: center; justify-content: center; color: #a75ded; flex-shrink: 0; }
.ari-welcome-eyebrow { font-size: 11px; font-weight: 600; color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.08em; }
.ari-welcome-title { font-size: 18px; font-weight: 700; color: #fff; }
.ari-welcome-sub { font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 2px; }
.ari-gen-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 18px; border-radius: 10px; border: none; cursor: pointer;
    background: linear-gradient(135deg,#a75ded,#7c3aed); color: #fff; font-size: 13px; font-weight: 700;
    transition: opacity 0.2s, transform 0.15s; flex-shrink: 0;
}
.ari-gen-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.ari-gen-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.ari-hide-xs { display: inline; }
.ari-show-xs { display: none; }
@media(max-width:420px){ .ari-hide-xs{display:none;} .ari-show-xs{display:inline;} }

/* stats */
.ari-stats { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
@media(min-width:768px){ .ari-stats{ grid-template-columns: repeat(4,1fr); } }
.ari-stat {
    background: rgba(20,10,40,0.92); border: 1px solid rgba(var(--ac),0.2); border-radius: 14px;
    padding: 16px 18px; position: relative; overflow: hidden;
}
.ari-stat::before { content: ''; position: absolute; inset: 0 0 auto 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(var(--ac),0.4),transparent); }
.ari-stat-glow { position: absolute; top: -35px; right: -35px; width: 100px; height: 100px; background: radial-gradient(circle,rgba(var(--ac),0.1) 0%,transparent 70%); pointer-events: none; }
.ari-stat-label { font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 6px; }
.ari-stat-val { font-size: 26px; font-weight: 800; color: #fff; }

/* card */
.ari-card {
    background: rgba(20,10,40,0.92); border: 1px solid rgba(167,93,237,0.18);
    border-radius: 18px; overflow: hidden; position: relative;
}
.ari-card::before { content: ''; position: absolute; inset: 0 0 auto 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.ari-card-glow { position: absolute; top: -60px; right: -60px; width: 200px; height: 200px; background: radial-gradient(circle,rgba(167,93,237,0.06) 0%,transparent 70%); pointer-events: none; }
.ari-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 22px 24px 16px; }
.ari-card-title { font-size: 16px; font-weight: 700; color: #fff; }
.ari-card-sub { font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 3px; }
.ari-total-badge { font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.4); white-space: nowrap; }

/* filters */
.ari-filters { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 24px 16px; border-bottom: 1px solid rgba(167,93,237,0.08); }
.ari-pill { padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; background: rgba(10,4,22,0.6); border: 1px solid rgba(167,93,237,0.15); color: rgba(255,255,255,0.5); transition: all 0.2s; }
.ari-pill:hover { color: #fff; border-color: rgba(167,93,237,0.35); }
.ari-pill--active { background: rgba(167,93,237,0.15); border-color: rgba(167,93,237,0.45); color: #c084fc; }

/* empty */
.ari-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 48px 16px; text-align: center; }
.ari-empty-icon { width: 72px; height: 72px; border-radius: 50%; background: rgba(167,93,237,0.08); border: 1px solid rgba(167,93,237,0.15); display: flex; align-items: center; justify-content: center; color: rgba(167,93,237,0.35); }
.ari-empty-title { font-size: 15px; font-weight: 600; color: rgba(255,255,255,0.6); }
.ari-empty-sub { font-size: 13px; color: rgba(255,255,255,0.3); }

/* recs list */
.ari-list { display: flex; flex-direction: column; }
.ari-rec { padding: 20px 24px; border-bottom: 1px solid rgba(167,93,237,0.06); transition: background 0.2s; }
.ari-rec:last-child { border-bottom: none; }
.ari-rec:hover { background: rgba(167,93,237,0.04); }
.ari-rec-main { display: flex; gap: 16px; }
.ari-rec-icon { width: 44px; height: 44px; border-radius: 12px; background: rgba(167,93,237,0.1); border: 1px solid rgba(167,93,237,0.2); display: flex; align-items: center; justify-content: center; color: #a75ded; flex-shrink: 0; }
.ari-rec-body { flex: 1; min-width: 0; }
.ari-rec-top { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
.ari-unread-dot { width: 7px; height: 7px; border-radius: 50%; background: #a75ded; flex-shrink: 0; }
.ari-rec-title { font-size: 15px; font-weight: 700; color: #fff; flex: 1; min-width: 0; }
.ari-rec-date { font-size: 11px; color: rgba(255,255,255,0.3); white-space: nowrap; }
.ari-rec-desc { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.6; margin-bottom: 12px; }
.ari-rec-stats { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
.ari-rec-stat { display: flex; flex-direction: column; gap: 2px; }
.ari-rec-stat-label { font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 0.06em; }
.ari-rec-stat-val { font-size: 14px; font-weight: 700; color: #fff; text-transform: capitalize; }
.ari-priority--high { color: #f87171; }
.ari-priority--medium { color: #fbbf24; }
.ari-priority--low { color: rgba(255,255,255,0.5); }

.ari-rec-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.ari-btn-view {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: 8px;
    background: linear-gradient(135deg,#a75ded,#7c3aed);
    color: #fff; font-size: 12px; font-weight: 700;
    text-decoration: none; transition: opacity 0.2s;
}
.ari-btn-view:hover { opacity: 0.9; }
.ari-btn-ghost {
    padding: 8px 16px; border-radius: 8px;
    background: rgba(10,4,22,0.6); border: 1px solid rgba(167,93,237,0.15);
    color: rgba(255,255,255,0.5); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: color 0.2s, background 0.2s;
}
.ari-btn-ghost:hover { color: #fff; background: rgba(167,93,237,0.08); }
.ari-status-tag { padding: 8px 16px; border-radius: 8px; font-size: 12px; font-weight: 700; text-transform: capitalize; }
.ari-status--active { background: rgba(34,197,94,0.12); color: #4ade80; }
.ari-status--dismissed { background: rgba(100,116,139,0.15); color: #94a3b8; }
.ari-status--applied { background: rgba(59,130,246,0.12); color: #60a5fa; }

/* pagination */
.ari-pagination { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 18px 24px; border-top: 1px solid rgba(167,93,237,0.08); }
.ari-page-info { font-size: 12px; color: rgba(255,255,255,0.35); }
.ari-page-btns { display: flex; flex-wrap: wrap; gap: 6px; }
.ari-page-btn { padding: 7px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; background: rgba(10,4,22,0.6); border: 1px solid rgba(167,93,237,0.15); color: rgba(255,255,255,0.5); cursor: pointer; transition: all 0.2s; }
.ari-page-btn:hover:not(.ari-page-btn--active):not(.ari-page-btn--disabled) { color: #fff; border-color: rgba(167,93,237,0.35); }
.ari-page-btn--active { background: rgba(167,93,237,0.2); border-color: rgba(167,93,237,0.5); color: #c084fc; cursor: default; }
.ari-page-btn--disabled { opacity: 0.3; cursor: not-allowed; }
</style>
